/**
 * Game result panel — DOM port of amgcommon_v2's `GameResultPanel`.
 *
 * In SicBo V2 the Flutter UI replaced the Rive WinLose artboard with this
 * Flutter widget (game_screen.dart:983-984). Four panel types share the
 * same widget: `startBet`, `noMoreBet`, `win`, `lose`. Only one is visible
 * at a time.
 */
export type GameResultType = 'startBet' | 'noMoreBet' | 'win' | 'lose';

export class GameResultPanel {
  private readonly host: HTMLElement;
  private readonly el: HTMLElement;
  private readonly headline: HTMLElement;
  private readonly amount: HTMLElement;
  private currentType: GameResultType | null = null;
  private hideTimer: number | null = null;

  constructor() {
    const host = document.getElementById('dom-hud');
    if (!host) throw new Error('GameResultPanel: #dom-hud missing');
    this.host = host;

    this.el = document.createElement('div');
    this.el.className = 'result-panel';

    this.headline = document.createElement('div');
    this.headline.className = 'result-panel-headline';

    this.amount = document.createElement('div');
    this.amount.className = 'result-panel-amount';

    this.el.append(this.headline, this.amount);
    this.host.appendChild(this.el);
  }

  show(type: GameResultType, amount: number = 0): void {
    if (this.hideTimer != null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.currentType = type;
    this.el.classList.remove('startBet', 'noMoreBet', 'win', 'lose');
    this.el.classList.add(type);

    switch (type) {
      case 'startBet':
        this.headline.textContent = 'PLACE YOUR BET';
        this.amount.textContent = '';
        // Auto-hide after 1.5s (matches Flutter panel auto-dismiss).
        this.hideTimer = window.setTimeout(() => this.hide(), 1500);
        break;
      case 'noMoreBet':
        this.headline.textContent = 'NO MORE BET';
        this.amount.textContent = '';
        this.hideTimer = window.setTimeout(() => this.hide(), 1500);
        break;
      case 'win':
        this.headline.textContent = 'YOU WIN';
        this.amount.textContent = `+${fmt(amount)}`;
        break;
      case 'lose':
        this.headline.textContent = 'YOU LOSE';
        this.amount.textContent = `-${fmt(Math.abs(amount))}`;
        break;
    }

    this.el.classList.add('visible');
  }

  hide(): void {
    if (this.hideTimer != null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.el.classList.remove('visible');
    this.currentType = null;
  }

  get visibleType(): GameResultType | null {
    return this.currentType;
  }
}

function fmt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}
